import React from 'react'
import { array, string, func, object } from 'prop-types'
import { css } from '@emotion/react'
import { SpacingToken } from '@livechat/design-system-react-components'

import { Carousel } from '@/components/Carousel/Carousel'
import { CarouselItem } from '@/components/Carousel/CarouselItem'
import { formatPrice } from '@/utils/products'
import Section from './Section'

const upgradesCss = css`
  display: flex;

  > div:first-of-type {
    margin-left: 0;
  }
`

const upgradeCss = (theme) => css`
  flex-grow: 1;
  border: 1px solid ${theme.colors.gray200};
  border-radius: 4px;
  padding: 20px;
  text-align: left;
  height: 128px;
  max-width: 300px;
`

const nameCss = (theme) => css`
  font-size: 18px;
  font-weight: ${theme.font.bold};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const priceCss = css`
  font-size: 16px;
`

const descriptionCss = css`
  font-size: 14px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  max-height: 40px;
`

const carouselCss = css`
  ul {
    column-gap: var(${SpacingToken.Spacing4});
  }
`

const slideCss = css`
  flex: 0 0 300px;
`

const Upgrade = ({ name, price, description }) => {
  return (
    <div css={upgradeCss} data-test="in-app-upgrades-elements">
      <div css={nameCss}>{name} </div>

      <div css={priceCss}>
        {price ? (
          <>
            <span>${formatPrice(price.cents)}</span>
            {price.months === 0 && <span> one-time payment</span>}
            {price.months === 1 && <span> / mo</span>}
            {price.months === 12 && <span> / yr</span>}
            {price.perAgent && <span>, per agent</span>}
          </>
        ) : (
          <span>FREE</span>
        )}
      </div>

      <div css={descriptionCss}>{description}</div>
    </div>
  )
}

Upgrade.propTypes = {
  name: string,
  price: object,
  description: string,
}

const Upgrades = ({ items, innerRef }) => {
  const chunks = []
  const slides = []
  const allItems = [...items]

  while (allItems.length) {
    chunks.push(allItems.splice(0, 1))
  }

  chunks.forEach((chunk) => {
    slides.push({
      type: 'custom',
      component: (
        <div css={upgradesCss}>
          {chunk.map((item) => (
            <Upgrade
              key={item.id}
              name={item.product.name}
              price={item.product.price}
              description={item.description}
            />
          ))}
        </div>
      ),
      id: chunk[0]?.id,
    })
  })

  return (
    <Section id="upgrades" title="In-App Upgrades" ref={innerRef}>
      <Carousel
        css={carouselCss}
        items={slides}
        renderItem={({ item, isSnapPoint }) => (
          <CarouselItem key={item.id} isSnapPoint={isSnapPoint} css={slideCss}>
            <div>{item.component}</div>
          </CarouselItem>
        )}
      />
    </Section>
  )
}

Upgrades.propTypes = {
  items: array,
  innerRef: func,
}

export default React.forwardRef((props, ref) => (
  <Upgrades innerRef={ref} {...props} />
))
